/**
 * Libreria di componenti.
 *
 * Si scrive prima questa e poi le pagine: così la coerenza visiva è
 * strutturale, non affidata alla disciplina di chi costruisce la ventesima
 * schermata.
 */

import type { ButtonHTMLAttributes, HTMLAttributes, ReactNode } from "react";

export function cx(
  ...values: Array<string | false | null | undefined>
): string {
  return values.filter(Boolean).join(" ");
}

// ── Card ────────────────────────────────────────────────────────────────────

interface CardProps extends HTMLAttributes<HTMLDivElement> {
  tone?: "plain" | "accent" | "warn" | "danger" | "ok" | "sunken";
  size?: "md" | "lg";
  interactive?: boolean;
  bordered?: boolean;
}

const CARD_TONES: Record<NonNullable<CardProps["tone"]>, string> = {
  plain: "bg-surface",
  sunken: "bg-surface-muted",
  accent: "bg-accent-soft ring-1 ring-accent-line",
  warn: "bg-warn-soft",
  danger: "bg-danger-soft",
  ok: "bg-ok-soft",
};

export function Card({
  tone = "plain",
  size = "md",
  interactive,
  bordered = true,
  className,
  ...rest
}: CardProps) {
  return (
    <div
      className={cx(
        size === "lg" ? "rounded-card-lg" : "rounded-card",
        CARD_TONES[tone],
        bordered && tone === "plain" && "ring-1 ring-line",
        interactive &&
          "cursor-pointer transition-all duration-quick ease-soft hover:shadow-soft hover:ring-line-strong active:scale-[0.995]",
        className,
      )}
      {...rest}
    />
  );
}

/**
 * Il bottone del prodotto.
 *
 * Si chiama ancora `Pill` per non riscrivere quaranta file, ma a pillola non
 * lo è più: gli angoli sono quelli dei campi. Un bottone completamente tondo
 * è un linguaggio da app di messaggistica, e qui si approvano modifiche di
 * prezzo — l'oggetto deve somigliare a un pulsante, non a una caramella.
 *
 * Il pieno è **quasi nero**, non colorato: il colore in un'interfaccia di
 * numeri serve a dire che qualcosa non va, e se lo consuma il bottone di
 * conferma non è più disponibile per quello.
 */

interface PillProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: "primary" | "secondary" | "ghost" | "danger" | "dark";
  size?: "sm" | "md" | "lg";
  icon?: ReactNode;
  loading?: boolean;
  block?: boolean;
}

const PILL_VARIANTS: Record<NonNullable<PillProps["variant"]>, string> = {
  primary: "bg-accent text-ink-inverse shadow-soft hover:bg-accent-strong",
  secondary:
    "bg-surface text-ink ring-1 ring-line hover:bg-surface-muted hover:ring-line-strong",
  ghost: "text-ink-soft hover:bg-surface-muted hover:text-ink",
  danger:
    "bg-surface text-danger ring-1 ring-danger/25 hover:bg-danger hover:text-ink-inverse hover:ring-danger",
  dark: "bg-nav-soft text-ink-inverse hover:bg-nav-hover",
};

const PILL_SIZES: Record<NonNullable<PillProps["size"]>, string> = {
  sm: "h-8 px-3 text-[13px] gap-1.5",
  md: "h-9 px-3.5 text-sm gap-2",
  lg: "h-11 px-5 text-[15px] gap-2",
};

export function Pill({
  variant = "secondary",
  size = "md",
  icon,
  loading,
  block,
  className,
  children,
  disabled,
  ...rest
}: PillProps) {
  return (
    <button
      type="button"
      disabled={disabled || loading}
      className={cx(
        "inline-flex shrink-0 items-center justify-center rounded-field font-medium",
        "transition-all duration-quick ease-soft",
        "disabled:pointer-events-none disabled:opacity-45",
        "active:scale-[0.98]",
        PILL_SIZES[size],
        PILL_VARIANTS[variant],
        block && "w-full",
        className,
      )}
      {...rest}
    >
      {loading ? <Spinner /> : icon}
      {children}
    </button>
  );
}

function Spinner() {
  return (
    <span
      aria-hidden
      className="size-3.5 animate-spin rounded-full border-2 border-current border-t-transparent"
    />
  );
}

/** Bottone tondo da barra: telefono, altro, allegato. */
export function IconButton({
  label,
  tone = "muted",
  className,
  children,
  ...rest
}: ButtonHTMLAttributes<HTMLButtonElement> & {
  label: string;
  tone?: "muted" | "accent" | "sunken";
}) {
  const tones = {
    muted: "text-ink-soft hover:bg-surface-muted hover:text-ink",
    sunken:
      "bg-surface-muted text-ink-soft hover:bg-surface-sunken hover:text-ink",
    accent: "bg-accent text-ink-inverse shadow-soft hover:bg-accent-strong",
  } as const;

  return (
    <button
      type="button"
      aria-label={label}
      title={label}
      className={cx(
        "grid size-10 shrink-0 place-items-center rounded-field",
        "transition-all duration-quick ease-soft active:scale-95",
        tones[tone],
        className,
      )}
      {...rest}
    >
      {children}
    </button>
  );
}

// ── Controllo a segmenti (General / Archive della foto) ────────────────────

export function Segmented<T extends string>({
  options,
  value,
  onChange,
}: {
  options: Array<{ id: T; label: string; count?: number }>;
  value: T;
  onChange: (id: T) => void;
}) {
  return (
    <div className="flex min-w-0 gap-1 rounded-pill bg-surface-muted p-1">
      {options.map((option) => {
        const active = option.id === value;
        return (
          <button
            key={option.id}
            type="button"
            onClick={() => onChange(option.id)}
            className={cx(
              // Stretto su telefono: quattro voci con il conteggio devono
              // stare in 350 px, altrimenti spingono fuori tutta la scheda.
              "flex min-w-0 flex-1 items-center justify-center gap-1 whitespace-nowrap rounded-pill px-1.5 py-2 text-[12px] font-bold sm:gap-1.5 sm:px-3 sm:text-[13px]",
              "transition-all duration-calm ease-soft",
              active
                ? "bg-accent text-ink-inverse shadow-soft"
                : "text-ink-soft hover:text-ink",
            )}
          >
            {option.label}
            {option.count !== undefined ? (
              <span
                className={cx(
                  "tabular-nums",
                  active ? "text-ink-inverse/75" : "text-ink-faint",
                )}
              >
                {option.count}
              </span>
            ) : null}
          </button>
        );
      })}
    </div>
  );
}

// ── Stati ───────────────────────────────────────────────────────────────────

export type DotTone = "ok" | "wait" | "danger" | "warn" | "idle";

const DOT_TONES: Record<DotTone, string> = {
  ok: "bg-ok",
  wait: "bg-wait pulse-dot",
  danger: "bg-danger",
  warn: "bg-warn",
  idle: "bg-line-strong",
};

export function StatusDot({
  tone,
  className,
}: {
  tone: DotTone;
  className?: string;
}) {
  return (
    <span
      aria-hidden
      className={cx("size-2 shrink-0 rounded-full", DOT_TONES[tone], className)}
    />
  );
}

export function Badge({
  tone = "plain",
  children,
}: {
  tone?: "plain" | "accent" | "ok" | "warn" | "danger";
  children: ReactNode;
}) {
  const tones = {
    plain: "bg-surface-muted text-ink-soft",
    accent: "bg-accent text-ink-inverse",
    ok: "bg-ok-soft text-ok",
    warn: "bg-warn-soft text-warn",
    danger: "bg-danger-soft text-danger",
  } as const;

  return (
    <span
      className={cx(
        "inline-flex items-center rounded-pill px-2 py-0.5 text-[11px] font-bold tabular-nums",
        tones[tone],
      )}
    >
      {children}
    </span>
  );
}

/** Pallino con il numero, come i "3" accanto a Messages nella foto. */
export function CountDot({
  value,
  tone = "accent",
}: {
  value: number;
  tone?: "accent" | "muted";
}) {
  if (value <= 0) return null;
  return (
    <span
      className={cx(
        "grid size-5 shrink-0 place-items-center rounded-full text-[11px] font-bold tabular-nums",
        tone === "accent"
          ? "bg-accent text-ink-inverse"
          : "bg-surface-muted text-ink-soft",
      )}
    >
      {value > 99 ? "99+" : value}
    </span>
  );
}

// ── Miniature ───────────────────────────────────────────────────────────────

export function Thumb({
  src,
  alt,
  size = 44,
  rounded = "card",
  className,
}: {
  src: string | null;
  alt: string;
  size?: number;
  rounded?: "card" | "full";
  className?: string;
}) {
  const initials = alt
    .split(/\s+/)
    .slice(0, 2)
    .map((word) => word[0]?.toUpperCase() ?? "")
    .join("");

  return (
    <div
      className={cx(
        "shrink-0 overflow-hidden bg-surface-muted",
        rounded === "full" ? "rounded-full" : "rounded-field",
        className,
      )}
      style={{ width: size, height: size }}
    >
      {src ? (
        <img
          src={src}
          alt=""
          loading="lazy"
          className="size-full object-cover"
        />
      ) : (
        <div className="grid size-full place-items-center text-[11px] font-bold text-ink-faint">
          {initials}
        </div>
      )}
    </div>
  );
}

/** Avatar con iniziali su fondo derivato dal nome: due persone, due colori. */
export function Avatar({
  name,
  size = 36,
  src,
}: {
  name: string;
  size?: number;
  src?: string | null;
}) {
  const hue = [...name].reduce(
    (acc, char) => (acc * 31 + char.charCodeAt(0)) % 360,
    7,
  );
  const initials = name
    .split(/\s+/)
    .slice(0, 2)
    .map((word) => word[0]?.toUpperCase() ?? "")
    .join("");

  if (src) return <Thumb src={src} alt={name} size={size} rounded="full" />;

  return (
    <div
      className="grid shrink-0 place-items-center rounded-full font-bold text-ink-inverse"
      style={{
        width: size,
        height: size,
        fontSize: size * 0.36,
        background: `linear-gradient(135deg, hsl(${hue} 62% 62%), hsl(${(hue + 40) % 360} 62% 52%))`,
      }}
    >
      {initials}
    </div>
  );
}

// ── Ricerca ─────────────────────────────────────────────────────────────────

export function SearchField({
  value,
  onChange,
  placeholder = "Cerca…",
}: {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
}) {
  return (
    <div className="flex items-center gap-2 rounded-pill bg-surface-muted px-4 transition-colors duration-quick focus-within:bg-surface-sunken">
      <input
        type="search"
        value={value}
        onChange={(event) => onChange(event.target.value)}
        placeholder={placeholder}
        aria-label={placeholder}
        className="min-w-0 flex-1 bg-transparent py-2.5 text-sm text-ink outline-none placeholder:text-ink-faint [&::-webkit-search-cancel-button]:hidden"
      />
      <svg
        viewBox="0 0 20 20"
        fill="none"
        className="size-4 shrink-0 text-ink-faint"
        aria-hidden
      >
        <circle cx="9" cy="9" r="6" stroke="currentColor" strokeWidth="1.8" />
        <path
          d="m13.5 13.5 3 3"
          stroke="currentColor"
          strokeWidth="1.8"
          strokeLinecap="round"
        />
      </svg>
    </div>
  );
}

// ── Scheletri e vuoti ───────────────────────────────────────────────────────

export function Skeleton({ className }: { className?: string }) {
  return (
    <div
      className={cx("animate-pulse rounded-card bg-surface-muted", className)}
    />
  );
}

export function EmptyState({
  icon,
  title,
  hint,
  action,
}: {
  icon?: ReactNode;
  title: string;
  hint?: string;
  action?: ReactNode;
}) {
  return (
    <div className="flex flex-col items-center gap-3 px-6 py-16 text-center">
      {icon ? (
        <div className="grid size-14 place-items-center rounded-card-lg bg-surface-muted text-ink-faint">
          {icon}
        </div>
      ) : null}
      <p className="text-[15px] font-bold text-ink">{title}</p>
      {hint ? (
        <p className="max-w-sm text-sm leading-relaxed text-ink-soft">{hint}</p>
      ) : null}
      {action}
    </div>
  );
}

// ── Interruttore ────────────────────────────────────────────────────────────

export function Toggle({
  checked,
  onChange,
  label,
  tone = "accent",
  disabled,
}: {
  checked: boolean;
  onChange: (next: boolean) => void;
  label: string;
  tone?: "accent" | "danger";
  disabled?: boolean;
}) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={label}
      disabled={disabled}
      onClick={() => onChange(!checked)}
      className={cx(
        "relative h-7 w-12 shrink-0 rounded-pill transition-colors duration-calm ease-soft",
        "disabled:opacity-45",
        checked
          ? tone === "danger"
            ? "bg-danger"
            : "bg-accent"
          : "bg-line-strong",
      )}
    >
      <span
        className={cx(
          "absolute top-1 size-5 rounded-full bg-surface shadow-soft transition-all duration-calm ease-soft",
          checked ? "left-6" : "left-1",
        )}
      />
    </button>
  );
}
